import {
  type FilterOption,
  type RangeFilterOption,
  type SimpleFilterOption,
} from '~/ui/systems/filter/filterOption';

/**
 * Definition of a filter that is intended to be extended by different types.
 */
interface BaseFilter<T> {
  /**
   * The name of the filter used for display and query parameters.
   */
  name: string;

  /**
   * The type of the filter used to determine how it will be rendered.
   */
  type: string;

  /**
   * Determines if the filter should be included when rendering pills.
   */
  includeInPills?: boolean;

  /**
   * The maximum number of options that will be displayed. All remaining options
   * will still be rendered, but hidden behind a user-actionable element.
   */
  renderLimit?: number;

  /**
   * Determines if the filter should be displayed.
   */
  hidden?: boolean;

  /**
   * The list of available options for the filters.
   */
  options: T[];
}

/**
 * A filter with options of type {@link SimpleFilterOption} rendered as
 * checkboxes.
 */
export interface CheckboxFilter extends BaseFilter<SimpleFilterOption> {
  type: 'checkbox';
}

/**
 * A filter with options of type {@link SimpleFilterOption} rendered as radio
 * buttons.
 */
export interface RadioButtonFilter extends BaseFilter<SimpleFilterOption> {
  type: 'radio';
}

/**
 * A filter with options of type {@link RangeFilterOption} rendered as a range.
 */
export interface RangeFilter extends BaseFilter<RangeFilterOption> {
  type: 'range';
}

/**
 * A combination filter that provides any type of {@link Filter} (except itself)
 * as possible options.
 */
export interface ComboFilter extends BaseFilter<Filter> {
  type: 'combo';
  options: Exclude<Filter, ComboFilter>[];
}

export type Filter = CheckboxFilter | RadioButtonFilter | RangeFilter | ComboFilter;

/**
 * Returns a mapping of {@link Filter} names to their collection of
 * {@link FilterOption}. For a {@link ComboFilter}, it will use the name of each
 * sub-filter and not the top-level filter.
 *
 * @param filter The {@link Filter} to extract all {@link FilterOption} from.
 */
export function getFilterOptions(filter: Filter): Record<string, FilterOption[]> {
  switch (filter.type) {
    case 'combo':
      return filter.options.reduce(
        (acc, filter) => ({ ...acc, [filter.name]: filter.options }),
        {} as Record<string, FilterOption[]>,
      );
    default:
      return { [filter.name]: filter.options };
  }
}

export function getSelectedFilterOptions(filter: Filter): FilterOption[] {
  return Object.values(getFilterOptions(filter))
    .flatMap(options => options)
    .filter(option => option.selected);
}
